// Form building blocks for sign-in and account screens: password field with "show", new password
// with a strength hint, one-time code field.
import { useId, useState, type ReactNode, type RefObject } from 'react';
import { ApiClientError } from '../lib/http';
import { MIN_PASSWORD_LENGTH, passwordHint } from '../lib/password';
import { Icon } from './Icon';

interface PasswordInputProps {
  id: string;
  label: ReactNode;
  value: string;
  onChange: (value: string) => void;
  autoComplete: 'current-password' | 'new-password' | 'off';
  inputRef?: RefObject<HTMLInputElement | null>;
  invalid?: boolean;
  describedBy?: string;
  enterKeyHint?: 'go' | 'next' | 'done';
  disabled?: boolean;
}

export function PasswordInput({ id, label, value, onChange, autoComplete, inputRef, invalid, describedBy, enterKeyHint, disabled }: PasswordInputProps) {
  const [visible, setVisible] = useState(false);
  return (
    <div className="field">
      <label htmlFor={id}>{label}</label>
      <div className="password">
        <input
          ref={inputRef}
          id={id}
          type={visible ? 'text' : 'password'}
          autoComplete={autoComplete}
          autoCapitalize="off"
          autoCorrect="off"
          spellCheck={false}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          aria-invalid={invalid ? true : undefined}
          aria-describedby={describedBy}
          enterKeyHint={enterKeyHint}
          disabled={disabled}
        />
        <button
          type="button"
          className="icon-btn"
          onClick={() => setVisible((v) => !v)}
          aria-label={visible ? 'Skrij geslo' : 'Pokaži geslo'}
          aria-pressed={visible}
          aria-controls={id}
        >
          <Icon name={visible ? 'eyeOff' : 'eye'} />
        </button>
      </div>
    </div>
  );
}

/** Why the new password cannot be submitted yet (null: it can). */
export function newPasswordProblem(password: string, repeat: string, username = ''): string | null {
  const hint = passwordHint(password, username);
  if (hint.level === 0) return hint.text;
  if (password !== repeat) return 'Gesli se ne ujemata.';
  return null;
}

const LEVEL_CLASS = ['weak', 'fair', 'good', 'strong'] as const;

interface NewPasswordFieldsProps {
  idPrefix: string;
  password: string;
  repeat: string;
  onPassword: (value: string) => void;
  onRepeat: (value: string) => void;
  /** The user's login name (the password must not be it). */
  username?: string;
  label?: string;
  disabled?: boolean;
}

/** New password twice, with a live hint on its strength and the 12-character rule. */
export function NewPasswordFields({ idPrefix, password, repeat, onPassword, onRepeat, username, label = 'Novo geslo', disabled }: NewPasswordFieldsProps) {
  const hintId = useId();
  const hint = passwordHint(password, username);
  const mismatch = repeat.length > 0 && password !== repeat;
  return (
    <>
      <PasswordInput
        id={`${idPrefix}-new`}
        label={label}
        value={password}
        onChange={onPassword}
        autoComplete="new-password"
        describedBy={hintId}
        enterKeyHint="next"
        disabled={disabled}
      />
      <div className={`strength strength--${LEVEL_CLASS[hint.level]}${password ? '' : ' strength--empty'}`} id={hintId} aria-live="polite">
        <span className="strength__bar" aria-hidden="true">
          <span />
          <span />
          <span />
        </span>
        <span className="strength__text">{hint.text}</span>
      </div>
      <PasswordInput
        id={`${idPrefix}-repeat`}
        label="Ponovite geslo"
        value={repeat}
        onChange={onRepeat}
        autoComplete="new-password"
        invalid={mismatch}
        describedBy={mismatch ? `${idPrefix}-mismatch` : undefined}
        enterKeyHint="done"
        disabled={disabled}
      />
      {mismatch && (
        <p id={`${idPrefix}-mismatch`} className="field-hint field-hint--error">
          Gesli se ne ujemata.
        </p>
      )}
      <p className="muted small">
        Najmanj {MIN_PASSWORD_LENGTH} znakov. Dobro geslo je stavek iz več besed, ki ga ne uporabljate drugje – na primer »Zjutraj pijem kavo z
        mlekom«.
      </p>
    </>
  );
}

interface CodeInputProps {
  id: string;
  value: string;
  onChange: (value: string) => void;
  label?: ReactNode;
  inputRef?: RefObject<HTMLInputElement | null>;
  invalid?: boolean;
  describedBy?: string;
  /** Also a recovery code ("K7QM-2XWD-9PRT-HC4N") is accepted: letters allowed. */
  allowRecovery?: boolean;
  disabled?: boolean;
}

/** One-time code from the authenticator app (6 digits) or, where allowed, a recovery code. */
export function CodeInput({ id, value, onChange, label = 'Koda iz aplikacije za preverjanje', inputRef, invalid, describedBy, allowRecovery, disabled }: CodeInputProps) {
  return (
    <div className="field">
      <label htmlFor={id}>{label}</label>
      <input
        ref={inputRef}
        id={id}
        className="code-input"
        type="text"
        inputMode={allowRecovery ? 'text' : 'numeric'}
        autoComplete="one-time-code"
        autoCapitalize="off"
        autoCorrect="off"
        spellCheck={false}
        maxLength={allowRecovery ? 24 : 9}
        placeholder={allowRecovery ? '123456 ali K7QM-2XWD-9PRT-HC4N' : '123456'}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        aria-invalid={invalid ? true : undefined}
        aria-describedby={describedBy}
        enterKeyHint="go"
        disabled={disabled}
      />
    </div>
  );
}

/** A code that looks complete enough to send: 6 digits, or (with recovery) 16 letters/digits. */
export function codeLooksComplete(code: string, allowRecovery = false): boolean {
  const c = code.replace(/[\s-]+/g, '');
  return /^\d{6}$/.test(c) || (allowRecovery && /^[0-9a-z]{16}$/i.test(c));
}

/** Slovenian message for a failed account request. */
export function accountErrorText(err: unknown, fallback: string): string {
  if (err instanceof ApiClientError) {
    if (err.status === 0) return 'Ni povezave s strežnikom. Preverite internetno povezavo in poskusite znova.';
    if (err.status === 429) return err.message || 'Preveč poskusov. Poskusite čez nekaj minut.';
    return err.message || fallback;
  }
  return fallback;
}

/** Inline error under a form. */
export function FormError({ id, children }: { id?: string; children: ReactNode }) {
  return (
    <p id={id} className="form-error" role="alert">
      {children}
    </p>
  );
}
